import {Injectable, SecurityContext} from '@angular/core';
import {SanitizeService} from "../sanitize/sanitize.service";
import {InternationalizationService} from "../internationalization/internationalization.service";
import {
  AnimationSettingsModel,
  DialogComponent,
  DialogUtility,
  PositionDataModel
} from "@syncfusion/ej2-angular-popups";
import {ToastService} from "../toast/toast.service";
import {AlertDialogArgs, ButtonArgs, ConfirmDialogArgs} from "@syncfusion/ej2-popups/src/dialog/dialog";
import {EmitType} from "@syncfusion/ej2-base";

export interface AlertButtonArgs {
  icon?: string;
  cssClass?: string;
  text?: string;
}

export interface AlertArgs {
  type: 'Info' | 'Success' | 'Warning' | 'Danger';
  title?: string;
  content?: string;
  icon?: string;
  callback?: (result: string, data?: string) => void;
  okButton?: AlertButtonArgs;
  cancelButton?: AlertButtonArgs;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {

  navigateModalFormModal: DialogComponent;

  private animationSettings: AnimationSettingsModel = {
    effect: 'SlideTop',
    duration: 400,
  };

  private positionSettings: PositionDataModel = {
    X: 'center',
    Y: 'center'
  };

  private iconMapping = {
    Info: 'feed',
    Success: 'check_circle',
    Warning: 'emergency_home',
    Error: 'error',
    OkCancel: 'chat_info',
    YesNo: 'help',
    Prompt: 'border_color',
    OkOnly: 'info',
  };

  constructor(private sanitize: SanitizeService,
              private i18n: InternationalizationService,
              private toast: ToastService) {
    (window as any).DialogAPI = this;
  }

  setNavigateModalFormModal(navigateModalFormModal: DialogComponent) {
    this.navigateModalFormModal = navigateModalFormModal;
  }

  /**
   * Displays a servicely form in a modal
   * called from Navigate.js
   */
  modalForm(args: AlertDialogArgs): DialogComponent {

    let dialog = this.navigateModalFormModal;

    dialog.header = args.title;
    dialog.width = args.width;
    dialog.height = args.height;
    dialog.position = args.position;
    dialog.zIndex = args.zIndex;
    dialog.showCloseIcon = args.showCloseIcon;
    dialog.cssClass = ["legacy-modal-form", args.cssClass].filter(Boolean).join(" ");
    dialog.close.subscribe((e) => args.close(e));

    // Replace the content of the dialog with the new contents
    let contentEle = ((dialog as any).contentEle as HTMLElement);
    contentEle.replaceChild(args.content as HTMLElement, contentEle.firstChild);

    dialog.show();

    // Asking for another dialog too quickly (possibly fadeout animation has not finished) - wait a bit and try again.
    if (dialog.visible == false) {
        setTimeout(() => {
          dialog.show()
        }, 500);
    }

    return dialog;
  }

  /**
   * Ask method to display a yes/no dialog box. Method can be used in a normal callback style or as a promise.
   *
   * @param {string} message - The message to display in the dialog box.
   * @param {any} titleOrCallback - (Optional) The title of the dialog box or a callback function. Default value is "Please confirm".
   * @param {any} callback - (Optional) A callback function to be executed after the confirmation. Default value is null.
   *
   * @return {Promise<boolean>} A promise that resolves to a boolean indicating the user's confirmation (yes=true, no=false).
   */
  ask(message: string, titleOrCallback: any = "ng.service.alert.default-confirm-message", callback?: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.askLike("YesNo", message, titleOrCallback, (result) => {
        if (result === "yes") {
          resolve(true);
        } else {
          resolve(false);
        }
        if (callback != null) {
          callback(result);
        }
      });
    });
  }

  /**
   * Confirm method to display a confirmation dialog box. Method can be used in a normal callback style or as a promise.
   *
   * @param {string} message - The message to display in the dialog box.
   * @param {any} titleOrCallback - (Optional) The title of the dialog box or a callback function. Default value is "Please confirm".
   * @param {any} callback - (Optional) A callback function to be executed after the confirmation. Default value is null.
   *
   * @return {Promise<boolean>} A promise that resolves to a boolean indicating the user's confirmation (ok=true, cancel=false).
   */
  confirm(message: string, titleOrCallback: any = "ng.service.alert.default-confirm-message", callback?: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.askLike("OkCancel", message, titleOrCallback, (result) => {
        if (result === "ok") {
          resolve(true);
        } else {
          resolve(false);
        }
        if (callback != null) {
          callback(result);
        }
      });
    });
  }

  prompt(message: string, titleOrCallback: any = "ng.service.alert.default-value-message", callback?: any) {
    this.askLike("Prompt", message, titleOrCallback, callback);
  };

  askMessage(args: AlertArgs) {
    this.messageLike("YesNo", args, "ng.service.alert.default-confirm-message");
  }

  confirmMessage(args: AlertArgs) {
    this.messageLike("OkCancel", args, "ng.service.alert.default-confirm-message");
  }

  promptMessage(args: AlertArgs) {
    this.messageLike("Prompt", args, "ng.service.alert.default-value-message");
  }

  infoMessage(message, title = "ng.service.alert.default-information-message", callback?: any) {
    this.messageLike("Info", message, title, callback);
  };

  successMessage(message, title = "ng.service.alert.default-success-message", callback?: any) {
    this.messageLike("Success", message, title, callback);
  };

  warningMessage(message, title = "ng.service.alert.default-warning-message", callback?: any) {
    this.messageLike("Warning", message, title, callback);
  };

  errorMessage(message, title = "ng.service.alert.default-error-message", callback?: any) {
    this.messageLike("Error", message, title, callback);
  };

  /**
   * @deprecated use infoMessage instead
   */
  alert(message, title = "ng.service.alert.default-information-message") {
    this.messageLike("OkOnly", message, title);
  };

  /**
   * @deprecated use info instead
   */
  log(message) {
    this.toast.showInfo(message);
  };

  info(message) {
    this.toast.showInfo(message);
  };

  success(message) {
    this.toast.showSuccess(message);
  };

  warning(message) {
    this.toast.showWarning(message);
  };

  error(message) {
    this.toast.showError(message);
  };

  danger(message) {
    this.toast.showDanger(message);
  };

  private askLike(type: string, message: string, titleOrCallback?: any, callback?: any) {
    if (typeof titleOrCallback === "string") {
      this.messageLike(type, message, titleOrCallback as string, callback);
    } else {
      this.messageLike(type, message, "ng.service.alert.default-confirm-message", titleOrCallback);
    }
  }

  private messageLike(type: string, messageOrArgs: string | AlertArgs, title?: string, callback?: any) {
    const isArgs = typeof messageOrArgs === "object";
    const args = messageOrArgs as AlertArgs;

    title = isArgs ? args.title : title;
    title = this.i18n.getMessage(title);

    callback = isArgs? args.callback : callback;
    const message = isArgs ? args.content : messageOrArgs as string;
    const icon = isArgs ? ((args.icon ?? this.iconMapping[args?.type]) ?? 'info') : (this.iconMapping[type] ?? 'info');

    const titleWithIcon = `<i class="mi">${icon}</i>${title ?? ""}`;

    const argsType = args.type?.toLowerCase() ?? 'unknown';
    const alertCss = isArgs ? ` alert-service-${argsType}` : "";

    const sanitisedMessage = this.sanitize.sanitize(SecurityContext.HTML, message);
    const i18nMessage = this.i18n.getMessage(sanitisedMessage);

    const okButton = isArgs ? args.okButton : {};
    const cancelButton = isArgs ? args.cancelButton : {};

    if (okButton?.icon) {
      okButton.text = `<i class="mi">${okButton.icon}</i><span>${okButton.text}</span>`;
      delete okButton.icon;
    }

    if (cancelButton?.icon) {
      cancelButton.text = `<i class="mi">${cancelButton.icon}</i><span>${cancelButton.text}</span>`;
      delete cancelButton.icon;
    }

    const cssClass = `alert-service alert-service-${this.toKebabCase(type)}${alertCss}`;

    switch (type) {

      case "OkCancel": {

        let dialog = DialogUtility.confirm({
          cssClass: cssClass,
          title: titleWithIcon,
          content: i18nMessage,
          isModal: true,
          animationSettings: this.animationSettings,
          position: this.positionSettings,
          open: (a) => {
          },
          close: () => {
            if (!(dialog as any).callbackCalled) {
              callback && callback("cancel");
            }
          },
          okButton: {
            ...{
              text: this.i18n.getMessage("common.button.ok"),
              click: () => {
                callback && callback("ok");
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...okButton
          },
          cancelButton: {
            ...{
              text: this.i18n.getMessage("common.button.cancel"),
              cssClass: "e-outline",
              click: () => {
                callback && callback("cancel");
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...cancelButton
          },
        });
      }
        break;

      case "YesNo": {

        let dialog = DialogUtility.confirm({
          cssClass: cssClass,
          title: titleWithIcon,
          content: i18nMessage,
          isModal: true,
          animationSettings: this.animationSettings,
          position: this.positionSettings,
          open: (a) => {
          },
          close: () => {
            if (!(dialog as any).callbackCalled) {
              callback && callback("no");
            }
          },
          okButton: {
            ...{
              text: this.i18n.getMessage("common.button.yes"),
              click: () => {
                callback && callback("yes");
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...okButton
          },
          cancelButton: {
            ...{
              text: this.i18n.getMessage("common.button.no"),
              cssClass: "e-outline",
              click: () => {
                callback && callback("no");
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...cancelButton
          },
        });
      }
        break;

      case "Prompt": {

        let dialog = DialogUtility.confirm({
          cssClass: cssClass,
          title: titleWithIcon,
          content: `<p>${i18nMessage}</p><input type="text" id="prompt"/>`,
          isModal: true,
          animationSettings: this.animationSettings,
          position: this.positionSettings,
          open: (a) => {
          },
          close: () => {
            if (!(dialog as any).callbackCalled) {
              let prompt = (dialog as any).contentEle.querySelector(":scope #prompt").value;
              callback && callback("cancel", prompt);
            }
          },
          okButton: {
            ...{
              text: this.i18n.getMessage("common.button.ok"),
              click: () => {
                let prompt = (dialog as any).contentEle.querySelector(":scope #prompt").value;
                callback && callback("ok", prompt);
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...okButton
          },
          cancelButton: {
            ...{
              text: this.i18n.getMessage("common.button.cancel"),
              cssClass: "e-outline",
              click: () => {
                let prompt = (dialog as any).contentEle.querySelector(":scope #prompt").value;
                callback && callback("cancel", prompt);
                (dialog as any).callbackCalled = true;
                dialog.hide();
              }
            },
            ...cancelButton
          },

        });
      }
        break;

      case "Info":
      case "Success":
      case "Warning":
      case "Error":
      case "Danger": {

        // button class to replace the primary button
        const okButtonClass = {
          Info: 'e-info',
          Success: 'e-success',
          Warning: 'e-warning',
          Error: 'e-danger',
          Danger: 'e-danger',
        }[type];

        let dialog = DialogUtility.alert({
          cssClass: `alert-service-utility-modal ${cssClass}`,
          title: null,
          isModal: true,
          animationSettings: this.animationSettings,
          position: this.positionSettings,
          content: `
            <div class="alert-icon">
              <i class="mi">${icon}</i>
            </div>
            <div class="alert-content">
              <h4>${title}</h4>
              <p>${i18nMessage}</p>
            </div>
          `,
        });

        dialog.buttons = [
          // {
          //   isFlat: true,
          //   buttonModel: {
          //     content: this.i18n.getMessage("common.button.cancel"),
          //   },
          //   click: () => {
          //     dialog.hide();
          //   },
          // },
          {
            isFlat: false,
            buttonModel: {
              cssClass: okButtonClass,
              content: this.i18n.getMessage("common.button.ok"),
            },
            click: () => {
              callback && callback("ok");
              dialog.hide();
            },
          },
        ];
      }
        break;

      case "OkOnly":
      default:  {

        let dialog = DialogUtility.alert({
          cssClass: cssClass,
          title: titleWithIcon,
          content: i18nMessage,
          isModal: true,
          animationSettings: this.animationSettings,
          position: this.positionSettings,
          okButton: {
            ...{
              text: this.i18n.getMessage("common.button.ok"),
              click: () => {
                callback && callback("ok");
                dialog.hide();
              }
            },
            ...okButton
          },
        });
      }
    }
  }

  private toKebabCase(str: string) {
    return str.replace(/([A-Z])/g, "-$1").replace(/^-/, "").toLowerCase();
  }
}
